import { motion } from "framer-motion";
import { MapPin, Phone, Mail, ArrowUpRight, Instagram } from "lucide-react";
import {
  SERVICE_AREAS,
  CONTACT_EMAIL,
  TIKTOK_URL,
  INSTAGRAM_URL,
  BOOKING_URL,
} from "@/constants/site";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { scrollToSection } from "@/lib/scroll";

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const TikTokGlyph = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.6 3c.4 2.2 1.8 3.6 4 3.8v3c-1.6 0-3-.5-4-1.3v6.6c0 3.7-2.6 6-5.9 6-3 0-5.4-2.3-5.4-5.3 0-3.4 3-5.7 6.3-5.2v3.1c-1.5-.5-3.2.4-3.2 2.1 0 1.2 1 2.2 2.3 2.2 1.5 0 2.6-1.1 2.6-2.9V3h3.3z" />
  </svg>
);

const PlaceholderTag = () => (
  <span className="ml-2 border border-[#E60000]/50 px-1.5 py-0.5 align-middle text-[9px] font-bold uppercase tracking-[0.2em] text-[#E60000]">
    Placeholder — swap me
  </span>
);

export const Contact = () => {
  const { config } = useSiteConfig();
  const { phone, email } = config.contact;
  return (
  <footer id="contact" data-testid="contact-section" className="relative pt-24 md:pt-32">
    <div className="mx-auto max-w-7xl px-5 md:px-10">
      <div className="grid grid-cols-1 gap-14 md:grid-cols-12 md:gap-10">
        {/* Service area */}
        <div className="md:col-span-6">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="mb-4 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-[#E60000]"
          >
            <span className="h-px w-10 bg-[#E60000]" />
            Service area
          </motion.p>
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl"
          >
            We come to you.
          </motion.h2>

          <motion.ul
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="mt-8 divide-y divide-white/10 border-y border-white/10"
          >
            {SERVICE_AREAS.map((area) => (
              <li
                key={area}
                data-testid={`service-area-${area.split(",")[0].toLowerCase().replace(" ", "-")}`}
                className="group flex items-center gap-4 py-4 transition-[padding] duration-300 hover:pl-2"
              >
                <MapPin size={18} className="shrink-0 text-[#E60000]" />
                <span className="font-display text-xl font-semibold uppercase tracking-wide text-white md:text-2xl">
                  {area}
                </span>
              </li>
            ))}
          </motion.ul>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="mt-6 text-sm leading-relaxed text-zinc-400"
          >
            Don't see your area?{" "}
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-ask-area-link"
              className="font-semibold text-white underline decoration-[#E60000] decoration-2 underline-offset-4 transition-colors duration-300 hover:text-[#E60000]"
            >
              Ask us
            </a>{" "}
            — we may still be able to help.
          </motion.p>
        </div>

        {/* Contact + socials */}
        <div className="md:col-span-6">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="border border-white/10 bg-white/[0.03] p-7 md:p-9"
          >
            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-zinc-500">
                  <Phone size={14} className="text-[#E60000]" />
                  Call or text
                </div>
                <a
                  href={`tel:${phone.replace(/\D/g, "")}`}
                  data-testid="contact-phone-link"
                  className="font-display mt-2 block text-2xl font-semibold text-white transition-colors duration-300 hover:text-[#E60000] md:text-3xl"
                >
                  {phone}
                </a>
              </div>
              <div className="h-px bg-white/10" />
              <div>
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-zinc-500">
                  <Mail size={14} className="text-[#E60000]" />
                  Email
                </div>
                <a
                  href={`mailto:${email}`}
                  data-testid="contact-email-link"
                  className="mt-2 block break-all text-base font-semibold text-white transition-colors duration-300 hover:text-[#E60000] md:text-lg"
                >
                  {email}
                  {email === CONTACT_EMAIL && <PlaceholderTag />}
                </a>
              </div>
            </div>
          </motion.div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <motion.a
              href={TIKTOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-tiktok-link"
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              className="group flex items-center justify-between border border-white/10 bg-[#0A0A0A] p-5 transition-[border-color,background-color] duration-300 hover:border-[#E60000]/60 hover:bg-[#140404]"
            >
              <span className="flex items-center gap-3">
                <TikTokGlyph size={20} />
                <span className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-white">
                  TikTok
                </span>
              </span>
              <ArrowUpRight size={18} className="text-[#E60000] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </motion.a>
            <motion.a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram-link"
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              className="group flex items-center justify-between border border-white/10 bg-[#0A0A0A] p-5 transition-[border-color,background-color] duration-300 hover:border-[#E60000]/60 hover:bg-[#140404]"
            >
              <span className="flex items-center gap-3">
                <Instagram size={20} />
                <span className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-white">
                  Instagram
                </span>
              </span>
              <ArrowUpRight size={18} className="text-[#E60000] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </motion.a>
          </div>
        </div>
      </div>

      {/* Giant sign-off */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        className="mt-20 select-none border-t border-white/10 pt-14 text-center md:mt-28"
      >
        <div className="font-display text-outline text-[clamp(3.4rem,12vw,10rem)] font-bold uppercase leading-none tracking-tight transition-colors duration-700">
          Let's talk
        </div>
        <p className="mt-6 font-display text-sm uppercase tracking-[0.3em] text-zinc-400">
          Clean cars · Quality service · Professional results
        </p>
      </motion.div>

      <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 py-8 sm:flex-row">
        <span className="text-xs text-zinc-500">
          © {new Date().getFullYear()} MARR Mobile Detailing — Melbourne · Palm Bay · Satellite Beach, FL
        </span>
        <button
          onClick={() => scrollToSection("#home")}
          data-testid="footer-back-to-top"
          className="font-display text-xs font-semibold uppercase tracking-[0.25em] text-zinc-400 transition-colors duration-300 hover:text-[#E60000]"
        >
          Back to top ↑
        </button>
      </div>
    </div>
  </footer>
  );
};

export default Contact;
